/*
 * GA4 events for the October 2026 features (IWA site study, task T0b, 2026-10-19).
 * Loaded on every page by snippets/iwa-events.liquid. Built from iwa-theme's
 * assets/iwa-events.js (T0a): same events, parameters, data attributes and queue; only
 * the route to GA4 differs.
 *
 * Route: gtag('event', …, { send_to: G-55Z6DLYKGY }) from the Google & YouTube app's
 * gtag for every event: menu_click, chip_click, search_suggestion_click, view_item_list /
 * select_item for the homepage rows (view when a row is on screen, select on a product
 * click), select_item for the cart drawer's suggestions (list "cart_drawer_recs", before
 * the app's own add_to_cart) and for product clicks on collection pages,
 * add_to_wishlist, and the declarative events below. Item ids use the app's format
 * (shopify_TR_<product id>_<variant id>) so GA4 joins list clicks to the app's view_item
 * and add_to_cart. On collection pages the app sends view_item_list itself (list id =
 * collection id, name = collection title; new since the 18 Oct audit), so the grid sends
 * only select_item, with the same id and name.
 *
 * Queue: gtag and the GA4 config only exist once vital-loader has released the parked
 * scripts (assets/custom.min.js: first scroll, mouse move or touch, or 3 s after load).
 * Every event waits in one sessionStorage queue until window.dataLayer holds the GA4
 * config and gtag.js has loaded, then goes out in order.
 * - A click on a link that leaves the page (menu, chip, search suggestion, row card) is
 *   never sent from that page: gtag holds events for a few seconds and doesn't always
 *   send them while the page unloads (seen on TR, 2026-10-19). The next page in the tab
 *   sends it. gtag events carry click_page, the path the click happened on.
 * - Clicks that stay on the page (tabs, drawer suggestions, wishlist, quick view, a chip
 *   filtering in place) and views are sent from the page itself.
 * - A link that opens a new tab or another site is sent from the page, as a beacon.
 * - An event sent from the page stays queued until its own GA4 request is
 *   seen (gtag sends its batch ~5 s later and drops it when the page unloads first), or
 *   for 6 s at most; if the page goes before that, the next page sends it again.
 * Entries older than 30 minutes are dropped.
 *
 * Declarative events, for other components (attributes only, no JS of their own):
 * - [data-iwa-ev="<event>"]: a click sends <event>.
 * - [data-iwa-view="<event>"]: sends <event> once per page, when at least half of the
 *   element is on screen (a slide hidden in a gallery doesn't count).
 * - data-iwa-p-<name>="<value>": a parameter (dashes become underscores). A click also
 *   takes the parameters of its closest [data-iwa-view]; a view also takes those of the
 *   [data-iwa-ev] inside it that is checked (aria-checked, aria-pressed, aria-selected or
 *   aria-current). Only clicks count: a choice made with the arrow keys sends nothing.
 *   Used by the PDP Dimensions slide (snippets/size-guide): dimensions_view,
 *   dimensions_size_click.
 *
 * window.IWAEvents.debug() shows the queue (used by the T0b browser checks).
 */
(function () {
  'use strict';
  if (window.IWAEvents) return;

  var script = document.currentScript;
  var GA4 = (script && script.getAttribute('data-ga4')) || 'G-55Z6DLYKGY';
  var CURRENCY = (script && script.getAttribute('data-currency')) || 'TRY';
  var PREFIX = (script && script.getAttribute('data-item-prefix')) || 'shopify_TR_';
  var ACK_MS = 6000;
  var KEY = 'iwa-events-queue';
  var MAX_AGE = 30 * 60 * 1000;
  var MAX_QUEUE = 40;
  // This page load; a link click is stored under it and sent by any other page.
  var PAGE = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

  var canStore = (function () {
    try {
      sessionStorage.setItem(KEY + '-test', '1');
      sessionStorage.removeItem(KEY + '-test');
      return true;
    } catch (e) {
      return false;
    }
  })();
  var memory = [];
  var seq = 0;
  var configAt = 0;
  var scanned = 0;
  var timer = null;
  var polls = 0;
  var sent = [];
  var buffer = null;
  var hint = null;

  // ---------------------------------------------------------------------------
  // Queue. sessionStorage is the one copy, so a page restored from the back/forward
  // cache or a second flush never sends an entry twice.

  function load() {
    if (!canStore) return memory.slice();
    try {
      var q = JSON.parse(sessionStorage.getItem(KEY) || '[]');
      var now = Date.now();
      return Array.isArray(q)
        ? q.filter(function (e) {
            return e && e.id && e.n && e.p && now - e.t < MAX_AGE;
          })
        : [];
    } catch (e) {
      return [];
    }
  }

  function save(q) {
    if (!canStore) {
      memory = q;
      return;
    }
    try {
      if (q.length) sessionStorage.setItem(KEY, JSON.stringify(q));
      else sessionStorage.removeItem(KEY);
    } catch (e) {}
  }

  // Ready once the app has defined gtag, queued the GA4 property's config and gtag.js has
  // loaded (google_tag_manager[GA4]); 3 s after the config without that, ready anyway.
  function isReady() {
    var dl = window.dataLayer;
    if (!configAt) {
      if (typeof window.gtag !== 'function' || !dl) return false;
      for (; scanned < dl.length; scanned++) {
        var c = dl[scanned];
        if (c && c[0] === 'config' && c[1] === GA4) {
          configAt = Date.now();
          break;
        }
      }
      if (!configAt) return false;
    }
    return !!(window.google_tag_manager && window.google_tag_manager[GA4]) || Date.now() - configAt > 3000;
  }

  function send(e) {
    // Kept until its request is seen (ack below); sp/sa = the page and time it went out.
    // Set before the call: gtag sometimes starts the request inside it.
    e.sp = PAGE;
    e.sa = performance.now();
    try {
      var p = { send_to: GA4 };
      for (var k in e.p) p[k] = e.p[k];
      if (e.b) p.transport_type = 'beacon';
      window.gtag('event', e.n, p);
      sent.push(e.n);
    } catch (err) {}
  }

  function flush() {
    var q = load();
    if (!q.length || !isReady()) return q.length;
    var fresh = false;
    q.forEach(function (e) {
      // A link click is never sent from the page it leaves: gtag holds events for a
      // few seconds and doesn't send them while the page unloads (seen on TR).
      if (e.next && e.pg === PAGE) return;
      // Sent from this page and waiting for its request.
      if (e.sp === PAGE) return;
      send(e);
      fresh = true;
    });
    save(q);
    if (fresh) setTimeout(ack, ACK_MS + 100);
    return q.length;
  }

  // gtag batches events for ~5 s and drops the batch when the page unloads first. An event
  // sent from this page leaves the queue once its own GA4 request shows up in the resource
  // timing, or after 6 s; until then the next page would send it again. Its own request:
  // our events go out one per request with the name in the URL (en=…) and click_page
  // among the URL parameters, which the app's events never carry; a request that only
  // started after it is not enough (dev preview 2026-10-19: the app's view_item_list
  // batch went out in between and the chip_click it seemed to ack was never sent).
  function ack(name, start) {
    var now = performance.now();
    var q = load();
    var hit = -1;
    q.forEach(function (e, i) {
      if (name && e.sp === PAGE && e.n === name && e.sa <= start && (hit < 0 || e.sa < q[hit].sa)) hit = i;
    });
    var keep = q.filter(function (e, i) {
      return i !== hit && !(e.sp === PAGE && now - e.sa >= ACK_MS);
    });
    if (keep.length !== q.length) save(keep);
  }

  if (typeof PerformanceObserver === 'function') {
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (en) {
          var u = en.name;
          if (u.indexOf('/g/collect') === -1 || u.indexOf('tid=' + GA4) === -1 || u.indexOf('ep.click_page=') === -1) return;
          var m = /[?&]en=([^&]+)/.exec(u);
          if (m) ack(decodeURIComponent(m[1].replace(/\+/g, ' ')), en.startTime);
        });
      }).observe({ type: 'resource', buffered: true });
    } catch (e) {}
  }

  // Until GA4 is ready, look every 0.5 s (for up to a minute) for entries to send, even
  // when there are none yet: a link click on the previous page may still be writing one.
  function kick() {
    flush();
    if (timer || (configAt && isReady())) return;
    polls = 0;
    timer = setInterval(function () {
      flush();
      if (++polls > 120 || (configAt && isReady())) {
        clearInterval(timer);
        timer = null;
      }
    }, 500);
  }

  // A link click that turned out to stay on the page (quick view, chip filter in place).
  function stayed(ids) {
    var q = load();
    q.forEach(function (e) {
      if (ids.indexOf(e.id) !== -1) delete e.next;
    });
    save(q);
    kick();
  }

  // mode: 'here' = send from this page once GA4 is ready; 'next' = a link click, sent by
  // the next page; 'beacon' = this page, with transport_type beacon (new tab, other site).
  function add(item, mode) {
    item.id = PAGE + '.' + ++seq;
    item.pg = PAGE;
    item.t = Date.now();
    if (mode === 'next' && canStore) item.next = true;
    if (mode === 'beacon' || (mode === 'next' && !canStore)) item.b = true;
    var q = load();
    q.push(item);
    if (q.length > MAX_QUEUE) q.splice(0, q.length - MAX_QUEUE);
    save(q);
    kick();
    return item.id;
  }

  // Events raised during a click are collected and added once the click shows whether it
  // leaves the page (see the click listener at the end).
  function queueItem(item) {
    if (buffer) buffer.push(item);
    else add(item, 'here');
  }

  function track(name, params) {
    var p = {};
    for (var k in params) {
      var v = params[k];
      if (v === undefined || v === null || v === '') continue;
      p[k] = typeof v === 'string' ? v.slice(0, 100) : v;
    }
    p.click_page = location.pathname.slice(0, 100);
    queueItem({ k: 'gtag', n: name, p: p });
  }

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) kick();
  });
  document.addEventListener('prerenderingchange', kick);

  // ---------------------------------------------------------------------------
  // Helpers

  function text(el) {
    return el ? (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100) : '';
  }

  function path(href) {
    if (!href) return '';
    try {
      var u = new URL(href, location.href);
      return (u.origin === location.origin ? u.pathname : u.host + u.pathname).slice(0, 100);
    } catch (e) {
      return '';
    }
  }

  // 1-based position of el among its parent's element children.
  function pos(el) {
    if (!el || !el.parentElement) return 0;
    return Array.prototype.indexOf.call(el.parentElement.children, el) + 1;
  }

  function handleOf(href) {
    var m = /\/products\/([^/?#]+)/.exec(href || '');
    return m ? decodeURIComponent(m[1]) : '';
  }

  // Short item JSON (snippets/iwa-events-item) → GA4 item. list: { id, name } or null.
  function item(it, list, position) {
    var out = {
      item_id: PREFIX + (it.i || '') + '_' + (it.vi || ''),
      item_name: String(it.n || '').slice(0, 100),
      item_brand: String(it.b || '').slice(0, 100),
      item_category: String(it.c || '').slice(0, 100),
      item_variant: String(it.v || '').slice(0, 100),
      price: Number(it.p) || 0,
      index: position
    };
    if (list) {
      out.item_list_id = list.id;
      out.item_list_name = list.name;
    }
    for (var k in out) if (out[k] === '') delete out[k];
    return out;
  }

  // A list element's GA4 list: data-iwa-list is the name, data-iwa-list-id the id (the
  // collection grid: the app's collection id and title); a row's id is its name.
  function listOf(el) {
    var name = (el.getAttribute('data-iwa-list') || location.pathname).slice(0, 100);
    return { id: (el.getAttribute('data-iwa-list-id') || name).slice(0, 100), name: name };
  }

  function parse(json) {
    try {
      return JSON.parse(json || 'null');
    } catch (e) {
      return null;
    }
  }

  function params(el) {
    var out = {};
    if (!el) return out;
    for (var i = 0; i < el.attributes.length; i++) {
      var a = el.attributes[i];
      if (a.name.indexOf('data-iwa-p-') === 0) out[a.name.slice(11).replace(/-/g, '_')] = a.value;
    }
    return out;
  }

  function merge(a, b) {
    for (var k in b) a[k] = b[k];
    return a;
  }

  // ---------------------------------------------------------------------------
  // Menu: the desktop mega menu (snippets/header-desktop-nav) and the phone drawer
  // (snippets/drawer-menu). Only links that navigate count; opening a sub-menu doesn't.

  function menu(t) {
    var nav = t.closest('ul.site-navigation');
    if (nav) {
      var el = t.closest('a[href], summary[data-link]');
      if (!el) return false;
      var top = el.closest('li.site-nav__item');
      var topLink = top && top.querySelector('.site-nav__link');
      var topLabel = text(topLink);
      var header = nav.closest('[data-section-type="header"]') || document;
      var tops = Array.prototype.slice.call(header.querySelectorAll('ul.site-navigation > li.site-nav__item'));
      var topPos = tops.indexOf(top) + 1;
      if (el.tagName === 'SUMMARY') {
        // With hover menus the top item's click goes to its collection; otherwise it only opens.
        var mode = el.parentElement && el.parentElement.getAttribute('data-hover');
        if (mode !== 'intent' && mode !== 'true') return true;
        hint = { force: 'next' };
        return menuEvent('mega', 1, topLabel, topPos, el.getAttribute('data-link'), topLabel);
      }
      if (el.closest('.megamenu')) {
        var card = el.closest('.iwa-mm-card');
        // The info block's "Shop all" button has position 0; the grid cards count from 1.
        var label = card ? text(card.querySelector('.iwa-mm-card__title') || card) : text(el);
        return menuEvent('mega', 2, label, card ? pos(card.closest('li')) : 0, el.href, topLabel + ' > ' + label);
      }
      return menuEvent('mega', 1, text(el), topPos, el.href, text(el));
    }

    var drawer = t.closest('#NavDrawer');
    if (drawer) {
      var a = t.closest('a[href]');
      if (!a || (!a.closest('ul.mobile-nav') && !a.classList.contains('header_nav_btn_label'))) return false;
      var li = a.closest('li.mobile-nav__item');
      var own = text(a.querySelector('span') || a) || text(a);
      if (!li) return menuEvent('drawer', 1, own, 0, a.href, own);
      var trail = [own];
      var level = 1;
      var up = li.parentElement && li.parentElement.closest('li.mobile-nav__item');
      while (up) {
        level++;
        trail.unshift(text(up.querySelector('.mobile-nav__faux-link, .mobile-nav__link')));
        up = up.parentElement && up.parentElement.closest('li.mobile-nav__item');
      }
      return menuEvent('drawer', level, own, pos(li), a.href, trail.join(' > '));
    }
    return false;
  }

  function menuEvent(type, level, label, position, href, trail) {
    track('menu_click', {
      menu_type: type,
      menu_level: level,
      menu_label: label,
      menu_position: position,
      menu_path: trail,
      link_url: path(href)
    });
    return true;
  }

  // ---------------------------------------------------------------------------
  // Chips: the collection header's chip row and image cards (snippets/menu-subcol-rows)
  // and the homepage rows' tabs (sections/product-feed).

  function chip(t) {
    var el = t.closest('a.menu-chips__chip');
    if (el) {
      var chips = el.closest('nav.menu-chips');
      // On its own collection a chip filters in place; elsewhere its script opens the
      // filtered collection, so a prevented click still leaves the page.
      if (!chips || !chips.hasAttribute('data-filter-here')) hint = { prevented: 'next' };
      return chipEvent('collection_header', el, text(el), el.closest('li'), chips);
    }
    el = t.closest('a.menu-subcol__link');
    if (el) return chipEvent('collection_cards', el, text(el), el.closest('li'), el.closest('nav.menu-subcol'));
    el = t.closest('button.iwa-prow__tab');
    if (el) {
      if (el.getAttribute('aria-selected') === 'true') return true;
      var count = el.querySelector('.iwa-prow__count');
      var label = text(el);
      if (count) label = label.slice(0, label.length - text(count).length).trim();
      var row = el.closest('.iwa-prow');
      var title = row && row.querySelector('.section-header__title');
      track('chip_click', {
        chip_group: 'home_row',
        chip_label: label,
        chip_position: pos(el),
        chip_set: text(title)
      });
      return true;
    }
    return false;
  }

  function chipEvent(group, el, label, li, set) {
    track('chip_click', {
      chip_group: group,
      chip_label: label,
      chip_position: pos(li),
      chip_set: set ? (set.getAttribute('aria-label') || '').slice(0, 100) : '',
      link_url: path(el.href)
    });
    return true;
  }

  // ---------------------------------------------------------------------------
  // Search popup (sections/search-modal, search-modal-results).

  var PANEL_TYPES = { products: 'product', articles: 'article', pages: 'page', collections: 'collection' };

  function search(t) {
    var modal = t.closest('search-modal');
    if (!modal) return false;
    var a = t.closest('a[href]');
    if (!a) return false;
    var input = modal.querySelector('input[name="q"]');
    var term = input ? input.value.trim() : '';
    var type;
    var label;
    var position = pos(a.closest('li'));
    var panel = a.closest('[data-sm-panel]');
    if (a.classList.contains('sm__suggest-link')) {
      type = 'suggested';
      label = text(a);
    } else if (a.classList.contains('sm-res__all')) {
      type = 'view_all';
      label = term;
      position = 0;
    } else if (panel) {
      type = PANEL_TYPES[panel.getAttribute('data-sm-panel')] || panel.getAttribute('data-sm-panel');
      label = text(a.querySelector('.sm-item__name') || a);
    } else {
      // "… için ara", shown when predictive search can't answer.
      type = 'query';
      label = term;
      position = 0;
    }
    track('search_suggestion_click', {
      suggestion_type: type,
      suggestion_text: label,
      suggestion_position: position,
      search_term: term,
      link_url: path(a.href)
    });
    return true;
  }

  // ---------------------------------------------------------------------------
  // Homepage rows (sections/product-feed, sections/inspired-row): each panel carries
  // data-iwa-list and data-iwa-items. Collection grid (snippets/collection-grid, collection
  // pages only): data-iwa-list + data-iwa-list-id on the grid, data-iwa-item on each card.

  function rowItems(panel) {
    if (!panel._iwaItems) {
      var list = listOf(panel);
      var seen = {};
      var out = [];
      (parse(panel.getAttribute('data-iwa-items')) || []).forEach(function (it) {
        if (!it || !it.h || seen[it.h]) return;
        seen[it.h] = true;
        out.push({ h: it.h, item: item(it, list, out.length + 1) });
      });
      panel._iwaItems = { list: list, items: out };
    }
    return panel._iwaItems;
  }

  function rowView(panel) {
    if (panel._iwaSeen) return;
    panel._iwaSeen = true;
    var row = rowItems(panel);
    if (!row.items.length) return;
    track('view_item_list', {
      item_list_id: row.list.id,
      item_list_name: row.list.name,
      items: row.items.map(function (x) {
        return x.item;
      })
    });
  }

  function rowClick(t) {
    var panel = t.closest('[data-iwa-list]');
    if (!panel) return false;
    var a = t.closest('a[href]');
    var h = a && handleOf(a.getAttribute('href'));
    if (!h) return false;
    if (!panel.hasAttribute('data-iwa-items')) {
      var card = a.closest('[data-iwa-item]');
      var own = card && panel.contains(card) && parse(card.getAttribute('data-iwa-item'));
      if (!own) return false;
      var list = listOf(panel);
      var cards = panel.querySelectorAll('[data-iwa-item]');
      selectItem(list, item(own, list, Array.prototype.indexOf.call(cards, card) + 1));
      return true;
    }
    var row = rowItems(panel);
    for (var i = 0; i < row.items.length; i++) {
      if (row.items[i].h === h) {
        selectItem(row.list, row.items[i].item);
        return true;
      }
    }
    return false;
  }

  function selectItem(list, it) {
    track('select_item', { item_list_id: list.id, item_list_name: list.name, items: [it] });
  }

  // ---------------------------------------------------------------------------
  // Cart drawer suggestions (snippets/cart-recommendations): the card's Add, quick view,
  // photo or title. One event per product per 10 s (a title click opens the quick view,
  // whose own Add is outside the card).

  var lastRec = { h: '', t: 0 };
  var REC_LIST = { id: 'cart_drawer_recs', name: 'cart_drawer_recs' };

  function recClick(t) {
    var card = t.closest('#CartDrawer cart-recommendations .iwa-cr__card[data-iwa-item]');
    if (!card) return false;
    if (!t.closest('[data-cr-add], [data-cr-quick-view], .iwa-cr__media, .iwa-cr__name, a.iwa-cr__add')) return false;
    var it = parse(card.getAttribute('data-iwa-item'));
    if (!it) return false;
    var now = Date.now();
    if (lastRec.h === it.h && now - lastRec.t < 10000) return true;
    lastRec = { h: it.h, t: now };
    selectItem(REC_LIST, item(it, REC_LIST, pos(card)));
    return true;
  }

  // ---------------------------------------------------------------------------
  // Wishlist (assets/wishlist.js, snippets/wishlist-button): a heart that isn't pressed
  // yet is an add. Read in the capture phase, before wishlist.js flips aria-pressed.
  // Product data comes from the row, drawer or collection card when there is one, else
  // from /products/<handle>.js (the selected variant on the product page).

  function wishlist(t) {
    var btn = t.closest('[data-wishlist-btn]');
    if (!btn) return false;
    if (btn.getAttribute('aria-pressed') === 'true' || btn.disabled) return true;
    var handle = btn.getAttribute('data-wishlist-handle') || '';
    var panel = btn.closest('[data-iwa-list][data-iwa-items]');
    if (panel) {
      var row = rowItems(panel);
      for (var i = 0; i < row.items.length; i++) {
        if (row.items[i].h === handle) return wishlistEvent(row.items[i].item);
      }
    }
    var card = btn.closest('[data-iwa-item]');
    var own = card && parse(card.getAttribute('data-iwa-item'));
    if (own) return wishlistEvent(item(own, null, 1));
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    // The product page heart (next to Add to cart) takes the variant chosen in its form.
    var scope = btn.closest('form') || btn.closest('[data-section-type="product"], .product-section');
    var form = scope && (scope.matches('form') ? scope : scope.querySelector('form[action*="/cart/add"]'));
    var chosen = form && form.querySelector('[name="id"]');
    fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (p) {
        var v = null;
        if (p && p.variants) {
          for (var j = 0; j < p.variants.length && !v; j++) {
            if (chosen && String(p.variants[j].id) === String(chosen.value)) v = p.variants[j];
          }
          for (var k = 0; k < p.variants.length && !v; k++) {
            if (p.variants[k].available) v = p.variants[k];
          }
          v = v || p.variants[0];
        }
        wishlistEvent(
          item(
            p && v
              ? {
                  s: v.sku || String(v.id),
                  n: p.title,
                  b: p.vendor,
                  c: p.type,
                  v: p.variants.length > 1 ? v.public_title || v.title : '',
                  p: v.price / 100,
                  i: p.id,
                  vi: v.id
                }
              : { n: handle, i: btn.getAttribute('data-wishlist-id') },
            null,
            1
          )
        );
      });
    return true;
  }

  function wishlistEvent(it) {
    var one = {};
    for (var k in it) if (k !== 'item_list_id' && k !== 'item_list_name') one[k] = it[k];
    track('add_to_wishlist', { currency: CURRENCY, value: one.price, items: [one] });
    return true;
  }

  // ---------------------------------------------------------------------------
  // Declarative events (data-iwa-ev, data-iwa-view)

  function declarativeClick(t) {
    var el = t.closest('[data-iwa-ev]');
    if (!el) return;
    track(el.getAttribute('data-iwa-ev'), merge(params(el.closest('[data-iwa-view]')), params(el)));
  }

  var viewsSent = {};

  function viewEvent(el) {
    var name = el.getAttribute('data-iwa-view');
    if (!name || viewsSent[name]) return;
    viewsSent[name] = true;
    var current = el.querySelector(
      '[data-iwa-ev][aria-checked="true"], [data-iwa-ev][aria-pressed="true"], [data-iwa-ev][aria-selected="true"], [data-iwa-ev][aria-current]:not([aria-current="false"])'
    );
    track(name, merge(params(el), params(current)));
  }

  // ---------------------------------------------------------------------------
  // Wiring

  // How a click on a link is sent: 'link' = decided after the click (see below), 'beacon'
  // = it opens a new tab or another site, so this page sends it, 'here' = no navigation.
  function linkMode(evt, a) {
    if (!a) return 'here';
    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#' || /^(javascript|mailto|tel|sms):/i.test(href) || a.hasAttribute('download')) return 'here';
    var u;
    try {
      u = new URL(a.href, location.href);
    } catch (e) {
      return 'here';
    }
    if (evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return 'beacon';
    if (a.target && a.target !== '_self') return 'beacon';
    if (u.origin !== location.origin) return 'beacon';
    if (u.pathname === location.pathname && u.search === location.search && u.hash) return 'here';
    return 'link';
  }

  document.addEventListener(
    'click',
    function (evt) {
      var t = evt.target;
      if (!t || !t.closest) return;
      buffer = [];
      hint = null;
      try {
        menu(t) || chip(t) || search(t) || recClick(t) || wishlist(t) || rowClick(t);
        declarativeClick(t);
      } catch (e) {}
      var items = buffer;
      var h = hint || {};
      buffer = null;
      hint = null;
      if (!items.length) return;
      var addAll = function (mode) {
        return items.map(function (it) {
          return add(it, mode);
        });
      };
      if (h.force) return addAll(h.force);
      var mode = linkMode(evt, t.closest('a[href]'));
      if (mode !== 'link') return addAll(mode);
      // A link click is stored for the next page straight away. Once the theme's own
      // handlers have run, one that was prevented (quick view, chip filter in place) is
      // sent from this page instead, unless the hint says the script navigates itself.
      var ids = addAll('next');
      setTimeout(function () {
        if (evt.defaultPrevented && (h.prevented || 'here') === 'here') stayed(ids);
      }, 0);
    },
    true
  );

  var observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (en) {
              // Half of the element, or half the screen for an element taller than two screens.
              var seen = en.intersectionRatio >= 0.5 || en.intersectionRect.height >= window.innerHeight * 0.5;
              if (!en.isIntersecting || !seen) return;
              observer.unobserve(en.target);
              if (en.target.hasAttribute('data-iwa-list')) rowView(en.target);
              else viewEvent(en.target);
            });
          },
          { threshold: [0, 0.1, 0.25, 0.5] }
        )
      : null;

  function watch() {
    if (!observer) return;
    var els = document.querySelectorAll('[data-iwa-list][data-iwa-items], [data-iwa-view]');
    for (var i = 0; i < els.length; i++) {
      if (els[i]._iwaWatched) continue;
      els[i]._iwaWatched = true;
      observer.observe(els[i]);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
  window.addEventListener('load', watch);

  kick();

  window.IWAEvents = {
    track: track,
    debug: function () {
      var q = load();
      var waiting = q.filter(function (e) {
        return e.sp === PAGE;
      }).length;
      return { ready: !!configAt && isReady(), queued: q.length - waiting, waiting: waiting, sent: sent.slice(-30) };
    }
  };
})();
